import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationService } from '../../application/OrganizationService';
import { Organization } from '../../domain/organization/Organization';

import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { UserActionType } from '../../context/user/actions';
import { useUserContext } from '../../context/user/context';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseOrganizationRegisterProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
}

type UseRegisterOrganizationResponse = ServiceResponse<(params: UseOrganizationRegisterProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useRegisterOrganization = (): UseRegisterOrganizationResponse => {
  const queryClient = useQueryClient();
  const { organizationDispatch } = useOrganizationContext();
  const { userDispatch } = useUserContext();
  const { projectDispatch } = useProjectContext();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isError, isSuccess, mutate } = useMutation(
    async ({ firstName, lastName, email, password, phone }: UseOrganizationRegisterProps): Promise<Organization> => {
      // Cleaning the possible previous session data ==== (from here)
      organizationDispatch({
        type: OrganizationActionType.RESET_ORGANIZATION,
      });
      userDispatch({
        type: UserActionType.RESET_USER,
      });
      projectDispatch({
        type: ProjectActionType.RESET_PROJECT,
      });
      await queryClient.invalidateQueries();
      // Cleaning the possible previous session data ==== (from to here)

      return organizationService.register({
        firstName,
        lastName,
        email: email.toLocaleLowerCase(),
        password,
        phone,
      });
    },
    {
      onSuccess: () => {
        showSnackbarMessage({
          type: `success`,
          messageIntlId: `infoerror.organizationregistersuccess`,
        });
      },
      onError: (err: any) => {
        console.info(`Organization Register Error: `, err?.response);
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ firstName, lastName, email, password, phone }: UseOrganizationRegisterProps) =>
      mutate({ firstName, lastName, email, password, phone }),
  };
};
